import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Trash2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ClientPhotoCaptureProps {
  /** Archivo seleccionado (ya comprimido por el padre, si aplica). */
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
  label?: string;
  hint?: string;
  className?: string;
  /** `default` = tema de la app; `light` = tarjeta blanca (flujo público). */
  variant?: 'default' | 'light';
}

/**
 * Captura de foto de perfil durante el alta de una clienta.
 *
 * La imagen se comprime en el navegador antes de subirla (ver
 * `compressImage`). El archivo se entrega al padre, que decide el momento
 * de subirlo: en el alta de personal del gym (`ClientsPage`) se sube
 * después de crear la clienta; en el flujo público (`JoinGymPage`) se sube
 * con el token de carga emitido por el RPC de alta.
 */
export function ClientPhotoCapture({
  file,
  onFileChange,
  disabled,
  label = 'Foto de perfil',
  hint = 'Opcional. Se comprime antes de subir.',
  className,
  variant = 'default',
}: ClientPhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isLight = variant === 'light';

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    e.target.value = '';
    onFileChange(selected);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <p className={cn('text-xs font-medium', isLight ? 'text-slate-400' : 'text-muted-foreground')}>
        {label}
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleSelect}
      />

      <div className="flex items-center gap-3">
        <div
          className={cn(
            'h-16 w-16 shrink-0 overflow-hidden rounded-full border flex items-center justify-center',
            isLight ? 'border-slate-100 bg-slate-50' : 'border-border bg-muted/40'
          )}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Vista previa" className="h-full w-full object-cover" />
          ) : (
            <User className={cn('h-6 w-6', isLight ? 'text-slate-300' : 'text-muted-foreground')} />
          )}
        </div>

        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className={cn(isLight && 'border-slate-200 text-slate-700 hover:bg-slate-50')}
          >
            <Camera className="mr-1.5 h-3.5 w-3.5" />
            {file ? 'Cambiar foto' : 'Subir foto'}
          </Button>

          {file && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              disabled={disabled}
              onClick={() => onFileChange(null)}
              title="Quitar foto"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {hint && (
        <p className={cn('text-xs', isLight ? 'text-slate-400' : 'text-muted-foreground')}>{hint}</p>
      )}
    </div>
  );
}
